<script setup lang="ts">

import { TESelect, TESwitch, TESwitchLabel } from '@ben/vite-te'
import { MapPinIcon } from '@heroicons/vue/24/outline'
import { Head } from '@inertiajs/vue3'

import type { City, DynamicFields, StrikeRequest } from '@/Types/Models/City.ts'
import type { GeneralSettings, StrikeSettings } from '@/Types/Models/Settings.ts'

const props = defineProps<{
  cities: City[]
  strikeFields: DynamicFields[],
  nextGlobalStrikeDate?: string
  strikeSettings: Pick<StrikeSettings, 'city_required'|'form_heading'|'form_sub_heading'|'form_general_title'|'form_general_subtitle'|'form_advanced_subtitle'|'form_advanced_title'|'privacy_title'|'privacy_subtitle'>
}>()

const form = useForm<Partial<StrikeRequest>>(
  {
    requester_name: '',
    requester_email: '',
    requester_phone: '',
    city: undefined,
    starts_at: '',
    date: props.nextGlobalStrikeDate ?? '',
    start_location: '',
    link: '',
    note: '',
    internal_note: '',
    location: undefined,
    fields: Object.fromEntries(props.strikeFields.map((f) => [f.name, ""])) satisfies Record<string, string>
  }
)

const showMore = ref(false)

const requiredData = [
  { key: 'requester_name', label: 'Dein Name', required: true },
  { key: 'requester_email', label: 'Deine E-Mail', required: true, type: 'email' },
  {
    key: 'requester_phone',
    label: 'Deine Handynummer (für die Streikzahlen-TF & Nachfragen)',
    required: true,
    type: 'phone'
  },
  { key: 'date', label: 'Datum', type: 'date', required: true }
  // { key: 'city_name', label: 'Stadt' },
]
const additionalData = [
  { key: 'starts_at', label: 'Startzeit', type: 'time' },
  { key: 'start_location', label: 'Startpunkt (z.b. Marktplatz)' },
  { key: 'note', label: 'Aktionsform (z.b. CleanUp, Die-In etc.)' },
  { key: 'link', label: 'Link für weitere Infos (z.b. Facebook-Event, Website etc.)', type: 'link' },
  ...props.strikeFields.map((field) => ({
    key: `fields.${field.name}`,
    label: field.label,
    required: field.required,
    type: field.rules === 'select'? 'select': 'text',
      options: Array.isArray(field.options) ? field.options : undefined
  }))
]

function submit() {
  if (props.strikeSettings.city_required && !form.city) {
    form.errors.city = 'Bitte wähle eine Ortsgruppe aus.'
    return
  }

  form.transform((data) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return { ...data, city_id: props.strikeSettings.city_required ? data.city!.id : undefined }
  }).post(route('requests.strike-request.store'), {
    onSuccess: () => {
      form.reset()
    }
  })
}

const showMap = ref(false)
function showMapStuff() {
  form.location =
    {
      type: 'Point',
      coordinates: [10, 51]
    }
  showMap.value = true
}
const acceptedPrivacyPolicy = ref(false)

const readyToSubmit = computed(() => {
  if (props.strikeSettings.city_required && !form.city) return false
  return acceptedPrivacyPolicy.value;

})

const settings = usePage().props.settings as GeneralSettings
</script>

<template>
  <CenterLayout>
    <template #header>
      <HeaderWithAction class="max-w-7xl mx-auto">

        <Head :title="settings.strike_word_singular" />

        <h1 class="text-2xl font-bold dark:text-white">{{strikeSettings.form_heading}}</h1>

        <h2 class="text-lg font-medium mt-4 text-gray-700 dark:text-gray-200" v-html="strikeSettings.form_sub_heading"/>
      </HeaderWithAction>
    </template>

    <form v-auto-animate class="-mx-4 sm:mx-0 inline-block space-y-4" @submit.prevent="submit">
      <Head :title="`${settings.strike_word_singular} eintragen`" />

      <TwoColumnFormSection
        :title="strikeSettings.form_general_title"
        :subtitle="strikeSettings.form_general_subtitle">
        <TESelect
          v-if="strikeSettings.city_required"
          v-model="form.city"
          name="city"
          :options="props.cities"
          :value-key="'id' as never"
          :display-key="'name' as never"
          label="Ortsgruppe"
          :error="form.errors.city"
        />

        <InputForData
          v-for="data in requiredData"
          :key="data.key"
          :data
          :form
        />

        <TESwitchLabel
          class="mt-4"
          :title="strikeSettings.privacy_title"
          :subtitle="strikeSettings.privacy_subtitle"
        >
          <TESwitch
            v-model="acceptedPrivacyPolicy"
          />
        </TESwitchLabel>

        <template #actions>
          <TEButton color="blue" @click="showMore = true">Uhrzeit etc. eintragen</TEButton>

          <TEButton primary :disabled="!readyToSubmit" @click="submit">Eintragen</TEButton>
        </template>

      </TwoColumnFormSection>

      <TwoColumnFormSection
        v-if="showMore"
        :title="strikeSettings.form_advanced_title"
        :subtitle="strikeSettings.form_advanced_subtitle">
        <InputForData
          v-for="data in additionalData"
          :key="data.key"
          :data
          :form
        />

        <div v-auto-animate>

          <button v-if="!showMap" class="underline cursor-pointer text-sm dark:text-zinc-100" type="button" @click="showMapStuff">Genauen Startpunkt
            auf
            der Karte setzen
          </button>

          <TEAlert v-if="showMap" type="info" title="Hinweis zum Kartenpunkt">
            <template #icon><MapPinIcon class="size-8" /></template>
            Verschiebe den Marker auf der Karte, oder klicke auf die Karte, um den genauen Startpunkt festzulegen. Sollte dies nicht klappen, verwende einen Computer mit Maus :)
          </TEAlert>

          <PointMap
            v-if="showMap && form.location"  v-model:coordinates="form.location.coordinates" is-draggable :initial-zoom="5"
            :height="400" />
        </div>

        <template #actions>
          <TEButton primary @click="submit">Eintragen</TEButton>
        </template>
      </TwoColumnFormSection>
    </form>
  </CenterLayout>
</template>
